import React, { Component, PropTypes } from 'react';
import { connect } from 'react-redux';

import authService from '../../state/services/auth';
import { fetchEvents } from '../../state/actions';
import {
  getEventList,
  getEventListCount,
  getEventListError,
  getEventListFetched,
  getEventListFetching,
  getEventListTotal,
} from '../../state/reducers';
import { isStringEqual } from '../../utils';

import EventList from './EventList.react';
import EventListCarousel from './EventListCarousel';
import LoadingEllipsis from '../LoadingEllipsis';
import { LoadMore } from '../LoadMore';
import EventSearchRecommendRace from '../EventSearchRecommendRace';

import { noResults } from './EventListContainer.module.css';

/**
 * Event list container element
 * Wraps the event list presentational component with Redux state
 * Dispatches actions fetching data, intitial and subsequent with load more button
 *
 * TODO: use custom event shape proptype
 * TODO: define query proptype shape, currently any parameters are accepted
 */

class EventListContainer extends Component {
  static propTypes = {
    /**
     * Boolean describing whether list should be rendered with carousel.
     */
    carousel: PropTypes.bool,
    /**
     * String class names to append to component styles.
     */
    className: PropTypes.string,
    /**
     * Dispatch method to fetch more events for current list
     */
    dispatchFetchEvents: PropTypes.func.isRequired,
    /**
     * Object representing current event list error state
     */
    error: PropTypes.shape(),
    /**
     * Array of event data objects currently loaded in state for this list
     */
    events: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    /**
     * Boolean describing whether the list has been fetched at all
     */
    fetched: PropTypes.bool.isRequired,
    /**
     * Boolean describing current state of list fetch request
     */
    fetching: PropTypes.bool.isRequired,
    /**
     * Callback when load more button is clicked
     */
    onLoadMore: PropTypes.func,
    /**
     * Object representing the current event list query to dispatch
     */
    query: PropTypes.shape(),
    /**
    * Boolean determining whether to show result count
    */
    showCount: PropTypes.bool,
    /**
    * Boolean determining whether to show load more
    */
    showLoadMore: PropTypes.bool,
    /**
     * Boolean determining whether to show sign up prompt
     */
    showPrompt: PropTypes.bool,
    /**
     * Optional string source location of list.
     */
    source: PropTypes.string,
    /**
     * Number of events for current list (and query) found on the server
     */
    total: PropTypes.number.isRequired,
  }

  static defaultProps = {
    carousel: false,
    className: '',
    data: {},
    error: null,
    query: undefined,
    showCount: true,
    showLoadMore: true,
    showPrompt: false,
    source: undefined,
  }

  componentDidMount() {
    if (!this.props.fetched) {
      this.fetchData();
    }
  }

  componentDidUpdate(prevProps) {
    if (!isStringEqual(this.props.query, prevProps.query)) {
      this.fetchData();
    }
  }

  fetchData = (append = false) => {
    const {
      dispatchFetchEvents,
      query,
    } = this.props;
    dispatchFetchEvents(query, append);
  }

  fetchMoreData = () => {
    this.fetchData(true);
  }

  render() {
    const {
      carousel,
      className,
      error,
      events,
      fetched,
      fetching,
      showCount,
      showLoadMore,
      showPrompt,
      source,
      total,
    } = this.props;

    const hasUserCookie = authService.hasUserCookie();

    if (fetching && !fetched) {
      return (
        <div>
          <LoadingEllipsis />
        </div>
      );
    }

    if (fetched && !events.length) {
      if (error) {
        return (
          <div>
            <p>Sorry, there was an error loading the events.</p>
          </div>
        );
      }
      return carousel
        ? (
          <div>
            <p>Sorry, no events were found.</p>
          </div>
        ) : (
          <div>
            <div className={noResults}>
              <p>{'No results'}</p>
            </div>
            <EventSearchRecommendRace isSearchPage className={'event-list__item'} />
          </div>
        );
    }

    return (
      <div className={className}>
        {!carousel ?
          <div>
            {showCount && <div className="number-of-results">{total} results</div>}
            <EventList
              events={events}
              total={total}
              hasUserCookie={hasUserCookie}
              showPrompt={showPrompt}
              source={source}
            />
            {showLoadMore && <LoadMore {...this.props} onClick={this.fetchMoreData} />}
          </div>
        :
          <EventListCarousel
            events={events}
            source={source}
          />
        }
      </div>
    );
  }
}

// Selectors are used so state shape is encapsulated within reducers
const mapStateToProps = (state, { query }) => ({
  count: getEventListCount(state, query),
  fetched: getEventListFetched(state, query),
  fetching: getEventListFetching(state, query),
  error: getEventListError(state, query),
  events: getEventList(state, query),
  total: getEventListTotal(state, query),
});

// Shorthand object definition, maps action creator to dispatch
const mapDispatchToProps = {
  dispatchFetchEvents: fetchEvents,
};

// Wrap in [react-redux connect](https://github.com/reactjs/react-redux/blob/master/docs/api.md#connect)
EventListContainer = connect(mapStateToProps, mapDispatchToProps)(EventListContainer);  // eslint-disable-line no-class-assign, max-len

export default EventListContainer;



// WEBPACK FOOTER //
// ./app/src/components/EventList/EventListContainer.react.js